import React, { useCallback, useMemo } from "react";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import { ChartData } from "veneer/Chart";
import List from "veneer/List";
import ListItem from "veneer/ListItem";

import ResourceLineChart from "./ResourceLineChart";
import { ChartPoint } from "../model";

interface TemperatureChartProps {
  data: ReadonlyArray<ChartPoint>;
  temperatureUnit: TemperatureUnit;
}

interface TemperaturePoint {
  x: number;
  y: number;
}

type TemperatureUnit = "c" | "f";

const DATA_CPU_LABEL = "CPU";
const DATA_DISK_LABEL = "Disk";

const UNIT_LABELS: Record<TemperatureUnit, string> = {
  c: "°C",
  f: "°F",
};

function toTemperature(value: number, unit: TemperatureUnit): number {
  return unit === "f" ? (value * 9) / 5 + 32 : value;
}

function getChartData({
  data,
  temperatureUnit,
}: TemperatureChartProps): ChartData<"line"> {
  const cpuData: TemperaturePoint[] = [];
  const diskData: TemperaturePoint[] = [];

  data.forEach(([timestamp, cpuTemperature, diskTemperature]) => {
    cpuData.push({
      x: timestamp,
      y: toTemperature(cpuTemperature, temperatureUnit),
    });
    diskData.push({
      x: timestamp,
      y: toTemperature(diskTemperature, temperatureUnit),
    });
  });

  const datasets: ChartData<"line">["datasets"] = [
    {
      borderWidth: 1,
      data: cpuData,
      label: DATA_CPU_LABEL,
      pointRadius: 0,
    },
  ];
  const diskZeroValue = temperatureUnit === "c" ? 0 : 32;
  const allDiskZero = diskData.every((point) => point.y === diskZeroValue);

  if (!allDiskZero) {
    datasets.push({
      borderWidth: 1,
      data: diskData,
      label: DATA_DISK_LABEL,
      pointRadius: 0,
    });
  }

  return { datasets };
}

function getYAxisMax(
  data: ReadonlyArray<ChartPoint>,
  temperatureUnit: TemperatureUnit,
): number {
  const maxValue = data.reduce((currentMax, point) => {
    const [, cpuTemperature, diskTemperature] = point;
    return Math.max(
      currentMax,
      toTemperature(cpuTemperature, temperatureUnit),
      toTemperature(diskTemperature, temperatureUnit),
    );
  }, 0);

  return maxValue + 10;
}

export default function TemperatureChart({
  data,
  temperatureUnit,
}: TemperatureChartProps) {
  const chartData = useMemo(
    () => getChartData({ data, temperatureUnit }),
    [data, temperatureUnit],
  );
  const timestamps = useMemo(
    () => data.map(([timestamp]) => timestamp),
    [data],
  );
  const unitLabel = UNIT_LABELS[temperatureUnit];
  const yAxisMax = useMemo(
    () => getYAxisMax(data, temperatureUnit),
    [data, temperatureUnit],
  );
  const formatTooltipValue = useCallback(
    (label: string, value: number) => {
      return `${label}: ${value.toFixed(2)} ${unitLabel}`;
    },
    [unitLabel],
  );
  const formatYAxisTick = useCallback(
    (value: string | number) => `${value} ${unitLabel}`,
    [unitLabel],
  );

  if (data.length === 0) {
    return (
      <Alert
        dense
        severity="info"
        title="Temperature data is not available for your device."
      >
        <Box mt={1}>
          <List disablePadding listStyleType="disc">
            <ListItem>
              To measure it, use Cortex player 4.0.0 or higher.
            </ListItem>
            <ListItem>
              On Windows 10, you must run the player as an administrator.
            </ListItem>
            <ListItem>
              Some hardware doesn't allow Cortex to read temperature, even with
              these steps.
            </ListItem>
            <ListItem>
              Your device must be online to update Fleet with the latest
              temperature data.
            </ListItem>
          </List>
        </Box>
      </Alert>
    );
  }

  return (
    <ResourceLineChart
      data={chartData}
      formatTooltipValue={formatTooltipValue}
      formatYAxisTick={formatYAxisTick}
      timestamps={timestamps}
      yAxisMax={yAxisMax}
      yAxisMin={0}
      yAxisStepSize={yAxisMax > 50 ? 10 : 5}
    />
  );
}
